import { useTranslation } from 'react-i18next';
import InformationIcon from 'components/atoms/icons/informationIcon';
import { Popover } from 'antd';
import { useState, type FC } from 'react';
import classNames from 'classnames';

import s from './styles.module.scss';

import type {
  TMightBeErroneousReason,
  TThresholdName,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { IPatientThresholds } from 'infrastructure/interfaces';

const getContent = (
  thresholdName: TThresholdName,
  thresholds: IPatientThresholds,
  mightBeErroneousReason?: TMightBeErroneousReason,
) => {
  if (mightBeErroneousReason) {
    return 'Weight outside of average reading time. Assess for accuracy.';
  }

  const dailyValue = thresholds[thresholdName];
  const weeklyValue = thresholds[thresholdName];
  const contentMap: Partial<Record<TThresholdName, string>> = {
    chfFiveDayWeightIncrease: `Weight gain ≥ ${weeklyValue} lbs in one week.`,
    chfDailyWeightIncrease: `Weight gain ≥ ${dailyValue} lbs in one day`,
  };

  return contentMap[thresholdName];
};

interface CHFRenalStatusProps {
  thresholdName?: TThresholdName | null;
  thresholds?: IPatientThresholds;
  mightBeErroneousReason?: TMightBeErroneousReason;
  /** Renders the `data-sub-status` attribute used to adjust adjacent status radius */
  isSubStatus?: boolean;
  className?: string;
  label?: 'CHFRenal' | 'weightMgmt';
}

const CHFRenalStatus: FC<CHFRenalStatusProps> = ({
  thresholdName,
  thresholds,
  mightBeErroneousReason,
  isSubStatus,
  className,
  label = 'CHFRenal',
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const canShowContent = !!thresholdName && !!thresholds;

  const Content = () =>
    canShowContent ? (
      <p className={s['popover-content']}>
        {getContent(thresholdName, thresholds, mightBeErroneousReason)}
      </p>
    ) : null;

  return (
    <Popover
      content={Content}
      rootClassName={s['popover-wrapper']}
      placement="bottomLeft"
      arrow={false}
      open={open}
      onOpenChange={(v) => {
        if (!mightBeErroneousReason) return;
        setOpen(v);
      }}
    >
      <div
        className={classNames(s['chf-wrapper'], className)}
        data-chf
        data-open={open}
        data-sub-status={isSubStatus || undefined}
      >
        <p className={s['chf-text']}>{t(`labels.${label}`)}</p>
        {mightBeErroneousReason && (
          <InformationIcon width={13} height={13} className={s.icon} />
        )}
      </div>
    </Popover>
  );
};

export default CHFRenalStatus;
